// ─── HormoneAddonPage.jsx ─────────────────────────────────────────────────────
// Dedicated page for the HORMONE & FRUCHTBARKEIT (FRAU) add-on.
// Supports 5 questionnaire states:
//   natural_cycle | hormonal_contraception | perimenopause | menopause | menopause_HRT
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ComposedChart, Area, Line, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ReferenceArea,
  ResponsiveContainer,
} from 'recharts';

import {
  tokens,
  MARKER_GROUPS,
  MARKER_CHART,
  PHASE_COLORS,
  CYCLE_DATA,
  MENOPAUSE_STAGES,
  MENOPAUSE_CURVES,
  HRT_BAND,
  STATE_CHART_MODE,
  getCyclePhase,
  getMenopausePosition,
  stateHasHRT,
  stateHasContraception,
} from './hormoneData';

// ─────────────────────────────────────────────────────────────────────────────
// PROP TYPES (JSDoc)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * @param {{
 *   user: {
 *     age: number,
 *     hormoneState: 'natural_cycle'|'hormonal_contraception'|'perimenopause'|'menopause'|'menopause_HRT',
 *     cycleDay: number|null,
 *     cycleLength: number,
 *     onHRT: boolean,
 *     hrtType: string|null,
 *     onContraception: boolean,
 *     contraceptionType: string|null,
 *     hasCycleTracker: boolean,
 *     irregularCycles: boolean,
 *   },
 *   hormoneResults: Array<{
 *     markerId: string,
 *     value: number,
 *     unit: string,
 *     referenceRangeLow: number,
 *     referenceRangeHigh: number,
 *     status: 'normal'|'low'|'high'|'optimal',
 *     phaseAdjustedStatus: string|null,
 *     historicalValues: Array<{ date: string, cycleDay: number|null, value: number }>
 *   }>,
 *   questionnaire: {
 *     symptoms: string[],
 *     mood: string,
 *     energy: string,
 *     libido: string,
 *   },
 *   aiNarrativeText: string|null,
 *   aiDoctorQuestion: string|null,
 * }} props
 */

// ─────────────────────────────────────────────────────────────────────────────
// SMALL SHARED COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

const Card = ({ children, style }) => (
  <div style={{
    background: tokens.bgCard,
    borderRadius: tokens.radius,
    border: `1px solid ${tokens.border}`,
    boxShadow: tokens.shadow,
    padding: '20px 24px',
    ...style,
  }}>
    {children}
  </div>
);

const Chip = ({ label, color = tokens.teal, bg = tokens.tealLight }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center',
    padding: '3px 10px', borderRadius: '99px',
    fontSize: 12, fontWeight: 600, letterSpacing: '0.02em',
    color, background: bg,
  }}>
    {label}
  </span>
);

const StatusBadge = ({ status, t }) => {
  const map = {
    normal:  { color: tokens.green,  bg: tokens.greenLight },
    optimal: { color: tokens.teal,   bg: tokens.tealLight  },
    low:     { color: tokens.amber,  bg: tokens.amberLight },
    high:    { color: tokens.red,    bg: tokens.redLight   },
  };
  const s = map[status] || map.normal;
  return <Chip label={t(`status.${status}`)} color={s.color} bg={s.bg} />;
};

const Banner = ({ icon, text, color = tokens.teal, bg = tokens.tealLight }) => (
  <div style={{
    display: 'flex', alignItems: 'flex-start', gap: 10,
    padding: '12px 16px', borderRadius: tokens.radiusSm,
    background: bg, border: `1px solid ${color}30`,
    fontSize: 13, color: tokens.textSecondary, lineHeight: 1.5,
  }}>
    <span style={{ fontSize: 16, flexShrink: 0 }}>{icon}</span>
    <span>{text}</span>
  </div>
);

const TabBar = ({ tabs, active, onChange }) => (
  <div style={{
    display: 'flex', gap: 4,
    borderBottom: `1px solid ${tokens.border}`,
    marginBottom: 20,
  }}>
    {tabs.map(tab => (
      <button
        key={tab.id}
        disabled={tab.disabled}
        onClick={() => !tab.disabled && onChange(tab.id)}
        style={{
          padding: '8px 16px 9px', border: 'none', background: 'none',
          borderBottom: active === tab.id ? `2px solid ${tokens.teal}` : '2px solid transparent',
          color: tab.disabled
            ? tokens.textMuted
            : active === tab.id ? tokens.teal : tokens.textSecondary,
          fontWeight: active === tab.id ? 600 : 400,
          fontSize: 13, cursor: tab.disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.15s',
        }}
      >
        {tab.label}
        {tab.disabled && (
          <span style={{
            marginLeft: 6, fontSize: 10, background: tokens.border,
            color: tokens.textMuted, padding: '1px 6px', borderRadius: 99,
          }}>
            {tab.disabledLabel}
          </span>
        )}
      </button>
    ))}
  </div>
);

const AccordionSection = ({ title, children, accent = tokens.teal }) => {
  const [open, setOpen] = useState(false);
  if (!children) return null;
  return (
    <div style={{ borderTop: `1px solid ${tokens.borderLight}`, paddingTop: 12, marginTop: 12 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 13, fontWeight: 600, color: accent, padding: 0, textAlign: 'left',
        }}
      >
        {title}
        <span style={{ fontSize: 10, transform: open ? 'rotate(180deg)' : 'none', transition: '0.2s' }}>▲</span>
      </button>
      {open && (
        <div style={{ marginTop: 10, fontSize: 13, color: tokens.textSecondary, lineHeight: 1.65 }}>
          {children}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SOFT ZONE INDICATOR (menopause position dot)
// ─────────────────────────────────────────────────────────────────────────────
const SoftZoneDot = ({ cx, cy, label }) => (
  <g>
    <circle cx={cx} cy={cy} r={22} fill={tokens.teal} fillOpacity={0.07} />
    <circle cx={cx} cy={cy} r={14} fill={tokens.teal} fillOpacity={0.13} />
    <circle cx={cx} cy={cy} r={7}  fill={tokens.teal} fillOpacity={0.55} />
    <circle cx={cx} cy={cy} r={3}  fill={tokens.teal} />
    {label && (
      <text x={cx} y={cy - 28} textAnchor="middle"
        fill={tokens.teal} fontSize={10} fontWeight={600}>
        {label}
      </text>
    )}
  </g>
);

// ─────────────────────────────────────────────────────────────────────────────
// CYCLE CHART
// ─────────────────────────────────────────────────────────────────────────────
const CycleChart = ({ marker, results, user, t }) => {
  const result = results.find(r => r.markerId === marker);
  const cfg    = MARKER_CHART[marker];
  if (!cfg?.hasCycleCurve) return null;

  const chartData = CYCLE_DATA.map(d => {
    const historical = (result?.historicalValues || []).find(h => h.cycleDay === d.day);
    const isCurrent  = user.cycleDay === d.day;
    return {
      day:     d.day,
      mid:     d[`${marker}mid`],
      low:     d[`${marker}low`],
      high:    d[`${marker}high`],
      dot:     historical?.value ?? undefined,
      current: isCurrent && result ? result.value : undefined,
    };
  });

  return (
    <ResponsiveContainer width="100%" height={200}>
      <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={tokens.borderLight} />

        {/* Phase backgrounds */}
        <ReferenceArea x1={1}  x2={5}  fill={PHASE_COLORS.menstrual.bg}  fillOpacity={0.6} />
        <ReferenceArea x1={6}  x2={12} fill={PHASE_COLORS.follicular.bg} fillOpacity={0.6} />
        <ReferenceArea x1={13} x2={15} fill={PHASE_COLORS.ovulatory.bg}  fillOpacity={0.6} />
        <ReferenceArea x1={16} x2={28} fill={PHASE_COLORS.luteal.bg}     fillOpacity={0.6} />

        {/* Ovulation reference line */}
        <ReferenceLine x={14} stroke={tokens.teal} strokeDasharray="4 2" strokeWidth={1} />

        {/* Reference band */}
        <Area type="monotone" dataKey="high" fill={cfg.color} stroke="none" fillOpacity={0.10} />
        <Area type="monotone" dataKey="low"  fill={tokens.bgCard} stroke="none" fillOpacity={1} />
        <Line type="monotone" dataKey="mid"  stroke={cfg.color} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />

        {/* Historical dots */}
        <Scatter dataKey="dot" fill={cfg.color} fillOpacity={0.5} />

        {/* Current result */}
        <Scatter dataKey="current" fill={cfg.color} />

        {/* Today line */}
        {user.cycleDay && (
          <ReferenceLine x={user.cycleDay} stroke={tokens.textMuted} strokeDasharray="2 2" />
        )}

        <XAxis
          dataKey="day"
          tick={{ fontSize: 10, fill: tokens.textMuted }}
          label={{ value: t('chart.dayLabel'), position: 'insideBottomRight', offset: -4, fontSize: 10, fill: tokens.textMuted }}
        />
        <YAxis tick={{ fontSize: 10, fill: tokens.textMuted }} width={38} unit={` ${cfg.unit}`} />
        <Tooltip
          contentStyle={{ fontSize: 12, borderRadius: 8, border: `1px solid ${tokens.border}` }}
          formatter={(v, name) => [
            `${v} ${cfg.unit}`,
            name === 'mid' ? t('chart.median') :
            name === 'current' ? t('chart.yourResult') :
            name === 'dot' ? t('chart.previousResult') : name,
          ]}
          labelFormatter={day => `${t('chart.day')} ${day}`}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MENOPAUSE TRANSITION CHART
// ─────────────────────────────────────────────────────────────────────────────
const MenopauseChart = ({ marker, results, user, t }) => {
  const result = results.find(r => r.markerId === marker);
  const cfg    = MARKER_CHART[marker];
  const curves = MENOPAUSE_CURVES[marker];
  if (!cfg?.hasMenoCurve || !curves) return null;

  const showHRT = stateHasHRT(user.hormoneState) || user.onHRT;

  // Soft zone dot position
  const fshResult = results.find(r => r.markerId === 'FSH');
  const dotX      = result ? getMenopausePosition(user.age, fshResult?.value) : null;

  const chartData = curves.natural.map((pt, i) => ({
    ...pt,
    ...(showHRT ? {
      hrtMid:  curves.hrt[i].mid,
      hrtLow:  curves.hrt[i].low,
      hrtHigh: curves.hrt[i].high,
    } : {}),
  }));

  // Compute dot y via linear interpolation
  const dotData = (() => {
    if (dotX === null || !result) return [];
    const fl = Math.floor(dotX);
    const fr = dotX - fl;
    const a  = curves.natural[Math.min(fl, 3)];
    const b  = curves.natural[Math.min(fl + 1, 3)];
    return [{ x: dotX, y: a.mid + (b.mid - a.mid) * fr }];
  })();

  return (
    <ResponsiveContainer width="100%" height={200}>
      <ComposedChart
        data={chartData}
        margin={{ top: 16, right: 8, left: -16, bottom: 0 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke={tokens.borderLight} />

        {/* Natural band */}
        <Area type="monotone" dataKey="high"    fill={cfg.color} stroke="none" fillOpacity={0.10} />
        <Area type="monotone" dataKey="low"     fill={tokens.bgCard} stroke="none" fillOpacity={1} />
        <Line type="monotone" dataKey="mid"     stroke={cfg.color} strokeWidth={2} dot={false} />

        {/* HRT band + line */}
        {showHRT && <>
          <Area type="monotone" dataKey="hrtHigh" fill={HRT_BAND.fill} stroke="none" fillOpacity={HRT_BAND.opacity} />
          <Area type="monotone" dataKey="hrtLow"  fill={tokens.bgCard} stroke="none" fillOpacity={1} />
          <Line type="monotone" dataKey="hrtMid"
            stroke={tokens.teal} strokeWidth={1.5} strokeDasharray="5 3" dot={false} />
        </>}

        {/* Soft zone indicator */}
        {dotData.length > 0 && (
          <Scatter
            data={dotData}
            dataKey="y"
            shape={({ cx, cy }) => (
              <SoftZoneDot cx={cx} cy={cy} label={t('chart.approximateZone')} />
            )}
          />
        )}

        <XAxis
          dataKey="x"
          type="number"
          domain={[0, 3]}
          ticks={[0, 1, 2, 3]}
          tickFormatter={i => t(`chart.menoStages.${MENOPAUSE_STAGES[i]?.labelKey}`)}
          tick={{ fontSize: 10, fill: tokens.textMuted }}
          interval={0}
        />
        <YAxis tick={{ fontSize: 10, fill: tokens.textMuted }} width={38} unit={` ${curves.unit}`} />
        <Tooltip
          contentStyle={{ fontSize: 12, borderRadius: 8, border: `1px solid ${tokens.border}` }}
          formatter={(v, name) => [
            `${v} ${curves.unit}`,
            name === 'mid'    ? t('chart.naturalMedian') :
            name === 'hrtMid' ? t('chart.hrtMedian') :
            name === 'y'      ? t('chart.yourZone') : name,
          ]}
          labelFormatter={x => {
            const idx = Math.round(x);
            return t(`chart.menoStages.${MENOPAUSE_STAGES[idx]?.labelKey}`);
          }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// BODY CONTEXT TABS
// ─────────────────────────────────────────────────────────────────────────────
const BodyContextTabs = ({ hormoneState, cyclePhase, t }) => {
  const [activeTab, setActiveTab] = useState('mood');
  const tabs = ['mood', 'energy', 'libido', 'pain'];

  const isCycle = ['natural_cycle', 'hormonal_contraception'].includes(hormoneState);
  const phase   = cyclePhase || 'follicular';
  const stage   =
    hormoneState === 'perimenopause' ? 'earlyPeri' :
    hormoneState === 'menopause'     ? 'menopause' : 'postmeno';

  const textKey = isCycle
    ? `bodyContext.cycle.${phase}.${activeTab}`
    : `bodyContext.meno.${stage}.${activeTab}`;

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 10 }}>
        {tabs.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '5px 12px', borderRadius: 99, cursor: 'pointer',
              border: `1px solid ${activeTab === tab ? tokens.teal : tokens.border}`,
              background: activeTab === tab ? tokens.tealLight : 'transparent',
              color: activeTab === tab ? tokens.teal : tokens.textSecondary,
              fontSize: 12, fontWeight: activeTab === tab ? 600 : 400,
            }}
          >
            {t(`bodyContext.tabs.${tab}`)}
          </button>
        ))}
      </div>
      <p style={{ fontSize: 13, color: tokens.textSecondary, lineHeight: 1.65, margin: 0 }}>
        {t(textKey)}
      </p>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CHART SECTION (cycle / transition / both with tabs)
// ─────────────────────────────────────────────────────────────────────────────
const ChartSection = ({ marker, results, user, t }) => {
  const mode   = STATE_CHART_MODE[user.hormoneState];
  const cfg    = MARKER_CHART[marker];
  const [tab, setTab] = useState('transition');

  const hasCycle = cfg?.hasCycleCurve && ['cycle', 'both'].includes(mode);
  const hasMeno  = cfg?.hasMenoCurve  && ['transition', 'both'].includes(mode);

  if (!hasCycle && !hasMeno) return null;

  const cyclePhase = getCyclePhase(user.cycleDay, user.cycleLength);

  // ── BOTH (perimenopause) ──
  if (mode === 'both') {
    const cycleDisabled = !user.hasCycleTracker;
    const tabs = [
      { id: 'transition', label: t('chart.tabs.transition') },
      {
        id:           'cycle',
        label:        t('chart.tabs.cycle'),
        disabled:     cycleDisabled,
        disabledLabel: t('chart.tabs.connect'),
      },
    ];

    return (
      <div style={{ marginTop: 16 }}>
        <TabBar tabs={tabs} active={tab} onChange={setTab} />

        {tab === 'transition' && (
          <>
            <MenopauseChart marker={marker} results={results} user={user} t={t} />
            <p style={{ fontSize: 12, color: tokens.textMuted, margin: '6px 0 0', fontStyle: 'italic' }}>
              {t('chart.approximateNote')}
            </p>
            <BodyContextTabs hormoneState={user.hormoneState} cyclePhase={null} t={t} />
          </>
        )}

        {tab === 'cycle' && cycleDisabled && (
          <div style={{
            padding: '32px 24px', textAlign: 'center',
            background: tokens.bgSoft, borderRadius: tokens.radiusSm,
          }}>
            <div style={{ fontSize: 30, marginBottom: 12 }}>📲</div>
            <div style={{ fontWeight: 700, color: tokens.textPrimary, marginBottom: 6 }}>
              {t('banners.connectTracker.title')}
            </div>
            <div style={{ fontSize: 13, color: tokens.textSecondary, maxWidth: 280, margin: '0 auto 16px' }}>
              {t('banners.connectTracker.body')}
            </div>
            <button style={{
              background: tokens.teal, color: '#fff', border: 'none',
              borderRadius: tokens.radiusSm, padding: '10px 20px',
              fontWeight: 600, fontSize: 13, cursor: 'pointer',
            }}>
              {t('banners.connectTracker.cta')}
            </button>
          </div>
        )}

        {tab === 'cycle' && !cycleDisabled && (
          <>
            {user.irregularCycles && (
              <div style={{ marginBottom: 10 }}>
                <Banner
                  icon="ℹ️"
                  text={t('banners.irregularCycle')}
                  color={tokens.amber}
                  bg={tokens.amberLight}
                />
              </div>
            )}
            <CycleChart marker={marker} results={results} user={user} t={t} />
            <BodyContextTabs hormoneState={user.hormoneState} cyclePhase={cyclePhase} t={t} />
          </>
        )}
      </div>
    );
  }

  // ── TRANSITION only ──
  if (hasMeno) {
    return (
      <div style={{ marginTop: 16 }}>
        <MenopauseChart marker={marker} results={results} user={user} t={t} />
        <p style={{ fontSize: 12, color: tokens.textMuted, margin: '6px 0 0', fontStyle: 'italic' }}>
          {t('chart.approximateNote')}
        </p>
        <BodyContextTabs hormoneState={user.hormoneState} cyclePhase={null} t={t} />
      </div>
    );
  }

  // ── CYCLE only ──
  return (
    <div style={{ marginTop: 16 }}>
      <CycleChart marker={marker} results={results} user={user} t={t} />
      <BodyContextTabs hormoneState={user.hormoneState} cyclePhase={cyclePhase} t={t} />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────
export default function HormoneAddonPage({
  user,
  hormoneResults = [],
  questionnaire  = {},
  aiNarrativeText,
  aiDoctorQuestion,
}) {
  const { t } = useTranslation('hormone');
  const [copied, setCopied] = useState(false);

  const hormoneState = user.hormoneState;

  const resultMap = useMemo(
    () => Object.fromEntries(hormoneResults.map(r => [r.markerId, r])),
    [hormoneResults],
  );

  const handleCopyQuestion = () => {
    if (!aiDoctorQuestion) return;
    navigator.clipboard.writeText(aiDoctorQuestion);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{
      maxWidth: 720, margin: '0 auto',
      padding: '24px 16px 56px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      color: tokens.textPrimary,
      background: tokens.bgPage,
      minHeight: '100vh',
    }}>

      {/* ── Page Header ─────────────────────────────────────── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: tokens.textPrimary }}>
            {t('page.title')}
          </h1>
          <Chip label={t(`stateChips.${hormoneState}`)} />
        </div>
        <p style={{ fontSize: 14, color: tokens.textSecondary, margin: 0, lineHeight: 1.5 }}>
          {t('page.subtitle')}
        </p>
      </div>

      {/* ── Contextual Banners ──────────────────────────────── */}
      {stateHasContraception(hormoneState) && (
        <div style={{ marginBottom: 16 }}>
          <Banner icon="💊" text={t('banners.contraception')} color={tokens.amber} bg={tokens.amberLight} />
        </div>
      )}
      {(stateHasHRT(hormoneState) || user.onHRT) && (
        <div style={{ marginBottom: 16 }}>
          <Banner icon="⚕️" text={t('banners.hrt')} />
        </div>
      )}

      {/* ── AI Narrative ────────────────────────────────────── */}
      {aiNarrativeText && (
        <Card style={{ marginBottom: 20, borderLeft: `4px solid ${tokens.teal}` }}>
          <div style={{
            fontSize: 11, fontWeight: 600, color: tokens.teal,
            textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8,
          }}>
            {t('page.yourSummary')}
          </div>
          <p style={{ fontSize: 14, lineHeight: 1.7, margin: 0, color: tokens.textPrimary }}>
            {aiNarrativeText}
          </p>
        </Card>
      )}

      {/* ── Marker Groups ───────────────────────────────────── */}
      {MARKER_GROUPS.map(group => (
        <div key={group.id} style={{ marginBottom: 36 }}>

          {/* Group header */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            marginBottom: 16, paddingBottom: 10,
            borderBottom: `2px solid ${group.color}25`,
          }}>
            <div style={{ width: 4, height: 22, borderRadius: 2, background: group.color, flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: tokens.textPrimary }}>
                {t(`markerGroups.${group.id}.label`)}
              </div>
              <div style={{ fontSize: 12, color: tokens.textMuted }}>
                {t(`markerGroups.${group.id}.sublabel`)}
              </div>
            </div>
          </div>

          {/* Markers */}
          {group.markers.map(markerId => {
            const result  = resultMap[markerId];
            const markerColor = MARKER_CHART[markerId]?.color || tokens.teal;

            return (
              <Card key={markerId} style={{ marginBottom: 14 }}>

                {/* Marker header row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 }}>
                      <span style={{ fontWeight: 700, fontSize: 15, color: tokens.textPrimary }}>
                        {t(`markers.${markerId}.name`)}
                      </span>
                      <span style={{
                        fontSize: 11, fontWeight: 600,
                        background: tokens.bgSoft, color: tokens.textMuted,
                        padding: '1px 7px', borderRadius: 99,
                      }}>
                        {t(`markers.${markerId}.abbr`)}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: tokens.textMuted }}>
                      {t(`markers.${markerId}.tagline`)}
                    </div>
                  </div>
                  {result && (
                    <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 14 }}>
                      <div style={{ fontWeight: 700, fontSize: 17, color: tokens.textPrimary }}>
                        {result.value}{' '}
                        <span style={{ fontSize: 12, fontWeight: 400, color: tokens.textMuted }}>
                          {result.unit}
                        </span>
                      </div>
                      <div style={{ marginTop: 4 }}>
                        <StatusBadge
                          status={result.phaseAdjustedStatus || result.status}
                          t={t}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* What is this */}
                <p style={{ fontSize: 13, color: tokens.textSecondary, lineHeight: 1.65, margin: '10px 0 0' }}>
                  {t(`markers.${markerId}.what`)}
                </p>

                {/* Across lifespan — state-specific */}
                <div style={{
                  margin: '12px 0 0', padding: '10px 14px',
                  background: tokens.bgSoft, borderRadius: tokens.radiusSm,
                  borderLeft: `3px solid ${markerColor}`,
                }}>
                  <div style={{
                    fontSize: 11, fontWeight: 600, color: tokens.textMuted,
                    textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4,
                  }}>
                    {t('markerSection.acrossLifespan')}
                  </div>
                  <p style={{ fontSize: 13, color: tokens.textSecondary, lineHeight: 1.65, margin: 0 }}>
                    {t(`markers.${markerId}.acrossLifespan.${hormoneState}`)}
                  </p>
                </div>

                {/* Chart */}
                <ChartSection
                  marker={markerId}
                  results={hormoneResults}
                  user={user}
                  t={t}
                />

                {/* Symptom connections */}
                <AccordionSection
                  title={t('markerSection.symptomConnections')}
                  accent={markerColor}
                >
                  {['cycle', 'fertility', 'libido', 'mood', 'energy'].map(sym => {
                    const text = t(`markers.${markerId}.symptomConnections.${sym}`, { defaultValue: '' });
                    if (!text) return null;
                    return (
                      <div key={sym} style={{ marginBottom: 10, display: 'flex', gap: 8 }}>
                        <span style={{
                          fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                          letterSpacing: '0.05em', color: markerColor,
                          flexShrink: 0, paddingTop: 2, minWidth: 54,
                        }}>
                          {t(`markerSection.symptoms.${sym}`)}
                        </span>
                        <span style={{ fontSize: 13, color: tokens.textSecondary, lineHeight: 1.6 }}>
                          {text}
                        </span>
                      </div>
                    );
                  })}
                </AccordionSection>

                {/* Fertility note — hidden for postmenopause */}
                {!['menopause', 'menopause_HRT'].includes(hormoneState) && (() => {
                  const text = t(`markers.${markerId}.fertilityNote`, { defaultValue: '' });
                  return text ? (
                    <AccordionSection title={t('markerSection.fertilityNote')} accent={tokens.green}>
                      {text}
                    </AccordionSection>
                  ) : null;
                })()}

                {/* Contraception note */}
                {stateHasContraception(hormoneState) && (() => {
                  const text = t(`markers.${markerId}.contraNote`, { defaultValue: '' });
                  return text ? (
                    <AccordionSection title={t('markerSection.contraNote')} accent={tokens.amber}>
                      {text}
                    </AccordionSection>
                  ) : null;
                })()}

                {/* HRT note */}
                {(stateHasHRT(hormoneState) || user.onHRT) && (() => {
                  const text = t(`markers.${markerId}.hrtNote`, { defaultValue: '' });
                  return text ? (
                    <AccordionSection title={t('markerSection.hrtNote')} accent={tokens.teal}>
                      {text}
                    </AccordionSection>
                  ) : null;
                })()}
              </Card>
            );
          })}
        </div>
      ))}

      {/* ── Doctor Question ─────────────────────────────────── */}
      {aiDoctorQuestion && (
        <Card style={{ borderLeft: `4px solid ${tokens.purple}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <div>
              <div style={{
                fontSize: 11, fontWeight: 600, color: tokens.purple,
                textTransform: 'uppercase', letterSpacing: '0.06em',
              }}>
                {t('doctor.sectionLabel')}
              </div>
              <div style={{ fontWeight: 700, fontSize: 15, color: tokens.textPrimary, marginTop: 3 }}>
                {t('doctor.title')}
              </div>
            </div>
            <button
              onClick={handleCopyQuestion}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '7px 14px', borderRadius: tokens.radiusSm,
                border: `1px solid ${tokens.border}`,
                background: copied ? tokens.greenLight : tokens.bgSoft,
                color: copied ? tokens.green : tokens.textSecondary,
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {copied ? '✓' : '⎘'}{' '}
              {copied ? t('doctor.copied') : t('doctor.copy')}
            </button>
          </div>
          <p style={{ fontSize: 14, color: tokens.textPrimary, lineHeight: 1.7, margin: '0 0 8px' }}>
            {aiDoctorQuestion}
          </p>
          <p style={{ fontSize: 12, color: tokens.textMuted, margin: 0 }}>
            {t('doctor.hint')}
          </p>
        </Card>
      )}
    </div>
  );
}
